import { useState, useCallback, createContext, useContext, ReactNode } from 'react';
import { telemetry } from '../../../telemetry';

const SIDEBAR_COLLAPSE_KEY = 'ai-agent-sidebar-collapsed';

interface SidebarCollapseContextType {
  isCollapsed: boolean;
  toggleCollapse: () => void;
  setCollapse: (collapsed: boolean) => void;
  forceCollapse: () => void;
}

const SidebarCollapseContext = createContext<SidebarCollapseContextType | null>(null);

export const SidebarCollapseProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSE_KEY);
      return stored ? JSON.parse(stored) : false;
    } catch (e) {
      telemetry.captureError(e);
      return false;
    }
  });

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev: boolean) => {
      const newValue = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSE_KEY, JSON.stringify(newValue));
      } catch (e) {
        telemetry.captureError(e);
      }
      return newValue;
    });
  }, []);

  const setCollapse = useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSE_KEY, JSON.stringify(collapsed));
    } catch (e) {
      telemetry.captureError(e);
    }
  }, []);

  // Force collapse temporarily without saving to localStorage (for artifact panel)
  const forceCollapse = useCallback(() => {
    setIsCollapsed(true);
  }, []);

  return (
    <SidebarCollapseContext.Provider
      value={{
        isCollapsed,
        toggleCollapse,
        setCollapse,
        forceCollapse,
      }}
    >
      {children}
    </SidebarCollapseContext.Provider>
  );
};

export const useSidebarCollapse = (): SidebarCollapseContextType => {
  const context = useContext(SidebarCollapseContext);
  if (!context) {
    throw new Error('useSidebarCollapse must be used within a SidebarCollapseProvider');
  }
  return context;
};
